/**
 * Client-side mirror of Core.Security EffectiveScopeCodec.
 * Encoded JWT/API scopes: `{tenantId:N}:{application}.{resource}:{mask}`
 * where mask bits are read=4, write=2, assign=1.
 */

const READ = 4;
const WRITE = 2;
const ASSIGN = 1;

const ACTION_BITS: { bit: number; action: string }[] = [
  { bit: READ, action: 'read' },
  { bit: WRITE, action: 'write' },
  { bit: ASSIGN, action: 'assign' },
];

export function expandEncodedScopes(encodedScopes: readonly string[]): Set<string> {
  const permissions = new Set<string>();

  for (const encoded of encodedScopes) {
    const decoded = tryDecode(encoded);
    if (!decoded) {
      continue;
    }

    for (const scope of expandPermissionScopes(
      decoded.application,
      decoded.resource,
      decoded.actionMask,
    )) {
      permissions.add(scope);
    }
  }

  return permissions;
}

/**
 * Maps hyphenated tenant ids → expanded permission scopes from encoded JWT scp values.
 */
export function toGrantsByTenant(
  encodedScopes: readonly string[],
): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>();

  for (const encoded of encodedScopes) {
    const decoded = tryDecode(encoded);
    if (!decoded) {
      continue;
    }

    const tenantId = formatTenantIdFromN(decoded.tenantId);
    if (!tenantId) {
      continue;
    }

    let permissions = result.get(tenantId);
    if (!permissions) {
      permissions = new Set<string>();
      result.set(tenantId, permissions);
    }

    for (const scope of expandPermissionScopes(
      decoded.application,
      decoded.resource,
      decoded.actionMask,
    )) {
      permissions.add(scope);
    }
  }

  return result;
}

/** Tenant ids (hyphenated) that include the given expanded permission scope. */
export function tenantsWithPermission(
  encodedScopes: readonly string[],
  permissionScope: string,
): string[] {
  const grants = toGrantsByTenant(encodedScopes);
  return [...grants.entries()]
    .filter(([, permissions]) => permissions.has(permissionScope))
    .map(([tenantId]) => tenantId)
    .sort((a, b) => a.localeCompare(b));
}

export function expandPermissionScopes(
  application: string,
  resource: string,
  actionMask: number,
): string[] {
  if (actionMask <= 0 || actionMask > (READ | WRITE | ASSIGN)) {
    return [];
  }

  const scopes: string[] = [];
  for (const { bit, action } of ACTION_BITS) {
    if ((actionMask & bit) === bit) {
      scopes.push(`${application}.${resource}.${action}`);
    }
  }

  return scopes;
}

/** Converts 32-char hex (Guid "N") to hyphenated Guid string. */
export function formatTenantIdFromN(tenantIdN: string): string | null {
  if (!/^[0-9a-fA-F]{32}$/.test(tenantIdN)) {
    return null;
  }

  const n = tenantIdN.toLowerCase();
  return `${n.slice(0, 8)}-${n.slice(8, 12)}-${n.slice(12, 16)}-${n.slice(16, 20)}-${n.slice(20)}`;
}

function tryDecode(
  encodedScope: string,
): { tenantId: string; application: string; resource: string; actionMask: number } | null {
  const firstSeparator = encodedScope.indexOf(':');
  if (firstSeparator <= 0 || firstSeparator >= encodedScope.length - 1) {
    return null;
  }

  const tenantId = encodedScope.slice(0, firstSeparator);
  if (!/^[0-9a-fA-F]{32}$/.test(tenantId)) {
    return null;
  }

  const remainder = encodedScope.slice(firstSeparator + 1);
  const secondSeparator = remainder.lastIndexOf(':');
  if (secondSeparator <= 0 || secondSeparator >= remainder.length - 1) {
    return null;
  }

  const applicationResource = remainder.slice(0, secondSeparator);
  const actionMask = Number.parseInt(remainder.slice(secondSeparator + 1), 10);
  if (!Number.isFinite(actionMask) || actionMask <= 0 || actionMask > 7) {
    return null;
  }

  const dot = applicationResource.indexOf('.');
  if (dot <= 0 || dot >= applicationResource.length - 1) {
    return null;
  }

  const application = applicationResource.slice(0, dot);
  const resource = applicationResource.slice(dot + 1);
  if (!application || !resource || resource.includes('.')) {
    return null;
  }

  return { tenantId, application, resource, actionMask };
}
